import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Lock, LogOut, Users, FileText, Shield, Bell, BarChart3,
  Eye, EyeOff, Check, X, Search, Filter, Download, Clock,
  AlertCircle, CheckCircle, Loader2, QrCode, Smartphone,
  ClipboardList, UserCheck, Activity, ChevronRight, Mail,
  Phone, Calendar, User, FileCheck, XCircle, Trash2, Plus,
  Settings, CheckSquare, Square, Globe, UserCog, Timer,
  MapPin, Pen, Cloud, FolderOpen, TrendingUp, Flag
} from 'lucide-react';

// Import new components
import StudyDashboard from '../components/StudyDashboard';
import ConsentTracking from '../components/ConsentTracking';
import DocumentStorage from '../components/DocumentStorage';
import SubjectProgressTracking from '../components/SubjectProgressTracking';
import AutomatedReminders from '../components/AutomatedReminders';
import CommunicationLog from '../components/CommunicationLog';
import MilestoneTemplates from '../components/MilestoneTemplates';
import VisitCalendar from '../components/VisitCalendar';
import BatchSubjectImport from '../components/BatchSubjectImport';
import AIAssistantPanel from '../components/AIAssistantPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== AUTH CALLBACK COMPONENT ====================
const AuthCallback = ({ onAuthComplete }) => {
  const hasProcessed = useRef(false);
  const location = useLocation();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        window.location.href = '/registry';
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        const response = await fetch(`${API_URL}/api/auth/google/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ session_id: sessionId })
        });

        const data = await response.json();

        if (response.ok && data.status === 'success') {
          // Clear the hash and redirect
          window.history.replaceState(null, '', '/registry');
          onAuthComplete(data.user);
        } else {
          alert(data.detail || 'Authentication failed');
          window.location.href = '/registry';
        }
      } catch (error) {
        console.error('Auth error:', error);
        window.location.href = '/registry';
      }
    };

    processAuth();
  }, [location.hash, onAuthComplete]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] to-[#1877F2] flex items-center justify-center">
      <div className="text-center text-white">
        <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" />
        <p className="text-lg">Completing sign in...</p>
      </div>
    </div>
  );
};

// ==================== REVEAL DETAIL MODAL ====================
const RevealDetailModal = ({ submission, onClose, onReveal, onPrescreen, onDecline, onConsent }) => {
  const [revealedData, setRevealedData] = useState(null);
  const [revealReason, setRevealReason] = useState('');
  const [showRevealForm, setShowRevealForm] = useState(false);
  const [showConsentForm, setShowConsentForm] = useState(false);
  const [consentVersion, setConsentVersion] = useState('1.0');
  const [studyId, setStudyId] = useState('ONC204');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleReveal = async () => {
    if (revealReason.length < 10) {
      setError('Reason must be at least 10 characters');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/registry/submissions/${submission.id}/reveal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason: revealReason })
      });

      if (!response.ok) {
        throw new Error('Failed to reveal submission');
      }

      const data = await response.json();
      setRevealedData(data);
      setShowRevealForm(false);
      onReveal && onReveal();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePrescreen = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/registry/submissions/${submission.id}/prescreen`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        onPrescreen && onPrescreen();
        onClose();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = async () => {
    if (!window.confirm('Are you sure you want to decline this submission?')) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/registry/submissions/${submission.id}/decline`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        onDecline && onDecline();
        onClose();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConsent = async () => {
    if (!revealedData) {
      setError('You must reveal the submission before recording consent');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/registry/submissions/${submission.id}/consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ consent_version: consentVersion, study_id: studyId })
      });

      if (!response.ok) {
        throw new Error('Failed to record consent');
      }

      const data = await response.json();
      alert(`Consent recorded!\nSSID: ${data.ssid}\nRID: ${data.rid}`);
      onConsent && onConsent();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto border-0 shadow-2xl">
        <CardHeader className="bg-gradient-to-r from-[#1877F2] to-[#0a1628] text-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl">Submission Detail</CardTitle>
              <p className="text-white/70 text-sm">ID: {submission.id.slice(0, 8)}...</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm flex items-center">
              <AlertCircle className="w-4 h-4 mr-2" />
              {error}
            </div>
          )}

          {/* Status Badge */}
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              submission.status === 'new' ? 'bg-orange-100 text-orange-700' :
              submission.status === 'prescreened' ? 'bg-blue-100 text-blue-700' :
              submission.status === 'enrolled' ? 'bg-green-100 text-green-700' :
              'bg-gray-100 text-gray-600'
            }`}>
              {submission.status.toUpperCase()}
            </span>
            <span className="text-sm text-gray-500">
              <Calendar className="w-4 h-4 inline mr-1" />
              {new Date(submission.created_at).toLocaleString()}
            </span>
          </div>

          {/* Masked Data Section */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
              <EyeOff className="w-4 h-4 mr-2 text-gray-500" />
              Masked Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500">Name</p>
                <p className="font-medium">{submission.first_initial}. {submission.last_name_masked}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Email</p>
                <p className="font-medium">{submission.email_masked}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Phone</p>
                <p className="font-medium">{submission.phone_masked || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Age</p>
                <p className="font-medium">{submission.age}</p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-gray-500">Condition</p>
                <p className="font-medium text-[#1877F2]">{submission.condition}</p>
              </div>
            </div>
          </div>

          {/* Revealed Data Section */}
          {revealedData ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-semibold text-green-800 mb-3 flex items-center">
                <Eye className="w-4 h-4 mr-2" />
                Revealed Information (Audit Logged)
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-green-600">Full Name</p>
                  <p className="font-medium text-green-900">{revealedData.first_name} {revealedData.last_name}</p>
                </div>
                <div>
                  <p className="text-xs text-green-600">Email</p>
                  <p className="font-medium text-green-900">{revealedData.email}</p>
                </div>
                <div>
                  <p className="text-xs text-green-600">Phone</p>
                  <p className="font-medium text-green-900">{revealedData.phone || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-xs text-green-600">Age</p>
                  <p className="font-medium text-green-900">{revealedData.age}</p>
                </div>
                {revealedData.free_text && (
                  <div className="col-span-2">
                    <p className="text-xs text-green-600">Additional Information</p>
                    <p className="font-medium text-green-900">{revealedData.free_text}</p>
                  </div>
                )}
              </div>
            </div>
          ) : showRevealForm ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h3 className="font-semibold text-amber-800 mb-3 flex items-center">
                <Eye className="w-4 h-4 mr-2" />
                Reveal Full Identifiers
              </h3>
              <p className="text-sm text-amber-700 mb-3">
                Revealing this submission will create an audit log entry. Please provide a reason.
              </p>
              <Textarea
                value={revealReason}
                onChange={(e) => setRevealReason(e.target.value)}
                placeholder="Enter reason for revealing (min 10 characters)..."
                className="mb-3"
                rows={3}
              />
              <div className="flex gap-2">
                <Button
                  onClick={handleReveal}
                  disabled={loading || revealReason.length < 10}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                  Reveal & Log
                </Button>
                <Button variant="outline" onClick={() => setShowRevealForm(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button
              onClick={() => setShowRevealForm(true)}
              variant="outline"
              className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              <Eye className="w-4 h-4 mr-2" />
              Reveal Full Identifiers (Requires Reason)
            </Button>
          )}

          {/* Consent Form */}
          {showConsentForm && revealedData && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-800 mb-3 flex items-center">
                <FileCheck className="w-4 h-4 mr-2" />
                Record Consent
              </h3>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-xs text-blue-600 block mb-1">Study ID</label>
                  <Input
                    value={studyId}
                    onChange={(e) => setStudyId(e.target.value)}
                    placeholder="e.g., ONC204"
                  />
                </div>
                <div>
                  <label className="text-xs text-blue-600 block mb-1">Consent Version</label>
                  <Input
                    value={consentVersion}
                    onChange={(e) => setConsentVersion(e.target.value)}
                    placeholder="e.g., 1.0"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleConsent}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileCheck className="w-4 h-4 mr-2" />}
                  Record Consent & Create Subject
                </Button>
                <Button variant="outline" onClick={() => setShowConsentForm(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3 pt-4 border-t">
            {submission.status === 'new' && (
              <>
                <Button onClick={handlePrescreen} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Mark Prescreened
                </Button>
                <Button onClick={handleDecline} disabled={loading} variant="outline" className="text-red-600 border-red-300 hover:bg-red-50">
                  <XCircle className="w-4 h-4 mr-2" />
                  Decline
                </Button>
              </>
            )}
            {submission.status === 'prescreened' && revealedData && (
              <Button
                onClick={() => setShowConsentForm(true)}
                disabled={loading}
                className="bg-green-600 hover:bg-green-700"
              >
                <FileCheck className="w-4 h-4 mr-2" />
                Record Consent
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== LOGIN COMPONENT ====================
const RegistryLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [totpSetup, setTotpSetup] = useState(null);
  const [step, setStep] = useState('login');

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/registry';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/registry/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, totp_code: totpCode || undefined })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }

      if (data.status === 'requires_totp_setup') {
        setTotpSetup({
          qrCode: data.totp_qr_code,
          secret: data.totp_secret
        });
        setStep('totp_setup');
      } else if (data.status === 'success') {
        onLogin(data.user);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTotpEnroll = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/registry/auth/enroll-totp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ totp_code: totpCode })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'TOTP enrollment failed');
      }

      const meResponse = await fetch(`${API_URL}/api/registry/auth/me`, {
        credentials: 'include'
      });
      const userData = await meResponse.json();
      onLogin(userData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // TOTP Setup Screen
  if (step === 'totp_setup' && totpSetup) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a1628] to-[#1877F2] flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-2xl">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-[#1877F2] rounded-full flex items-center justify-center mx-auto mb-4">
              <Smartphone className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">Set Up Two-Factor Auth</CardTitle>
            <p className="text-gray-500 text-sm mt-2">Scan the QR code with your authenticator app</p>
          </CardHeader>
          <CardContent>
            <div className="flex justify-center mb-4">
              <img src={totpSetup.qrCode} alt="TOTP QR Code" className="w-48 h-48" />
            </div>
            <div className="bg-gray-100 rounded-lg p-3 mb-4">
              <p className="text-xs text-gray-500 text-center mb-1">Or enter this code manually:</p>
              <p className="text-sm font-mono text-center font-bold tracking-wider">{totpSetup.secret}</p>
            </div>
            <form onSubmit={handleTotpEnroll} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm flex items-center">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  {error}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Enter 6-digit code</label>
                <Input
                  type="text"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="h-12 text-center text-2xl font-mono tracking-widest"
                  maxLength={6}
                  required
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#1877F2] hover:bg-[#166FE5] text-white h-11 font-semibold"
                disabled={loading || totpCode.length !== 6}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Verify & Complete Setup
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Login Screen
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] to-[#1877F2] flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-0 shadow-2xl">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 bg-[#1877F2] rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-gray-900">Registry Console</CardTitle>
          <p className="text-gray-500 text-sm">CDOC Clinical Research - Staff Only</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Google OAuth Button */}
          <Button
            type="button"
            variant="outline"
            className="w-full h-12 border-gray-300 hover:bg-gray-50"
            onClick={handleGoogleLogin}
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-gray-500">Or continue with email</span>
            </div>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm flex items-center">
                <AlertCircle className="w-4 h-4 mr-2" />
                {error}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="contact@cdoctele.com"
                className="h-11"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="h-11"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                TOTP Code <span className="text-gray-400">(if enrolled)</span>
              </label>
              <Input
                type="text"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="h-11 font-mono tracking-widest"
                maxLength={6}
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-[#1877F2] hover:bg-[#166FE5] text-white h-11 font-semibold"
              disabled={loading}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
              Sign In with Email
            </Button>
          </form>
          <p className="text-xs text-gray-400 text-center mt-4">
            Access restricted to authorized personnel only.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== MAIN REGISTRY CONSOLE ====================
const RegistryPage = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [submissions, setSubmissions] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [isOAuthCallback, setIsOAuthCallback] = useState(false);
  
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  
  // Staff emails state
  const [staffEmails, setStaffEmails] = useState([]);
  const [newStaffEmail, setNewStaffEmail] = useState('');
  const [staffEmailLoading, setStaffEmailLoading] = useState(false);
  const [staffEmailError, setStaffEmailError] = useState('');
  
  // Allowed domains state
  const [allowedDomains, setAllowedDomains] = useState([]);
  const [newDomain, setNewDomain] = useState('');
  const [domainLoading, setDomainLoading] = useState(false);
  
  // User management state
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  
  // Export state
  const [exportLoading, setExportLoading] = useState(false);
  const [exportReason, setExportReason] = useState('');
  const [showExportModal, setShowExportModal] = useState(false);
  
  // Session timeout state
  const [sessionInfo, setSessionInfo] = useState(null);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const sessionCheckRef = useRef(null);
  
  // Study templates state
  const [studyTemplates, setStudyTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Audit report state
  const [auditReportLoading, setAuditReportLoading] = useState(false);
  const [showAuditReportModal, setShowAuditReportModal] = useState(false);
  const [auditReportReason, setAuditReportReason] = useState('');
  
  // AI Assistant state
  const [showAIAssistant, setShowAIAssistant] = useState(false);  const [auditStartDate, setAuditStartDate] = useState('');
  const [auditEndDate, setAuditEndDate] = useState('');
  
  // Multi-site filter state
  const [siteFilter, setSiteFilter] = useState('all');
  const sites = [
    { id: 'all', name: 'All Sites' },
    { id: 'ny_main', name: 'New York Main' },
    { id: 'brooklyn', name: 'Brooklyn Clinic' },
    { id: 'queens', name: 'Queens Office' },
    { id: 'bronx', name: 'Bronx Center' },
  ];
  
  const location = useLocation();

  // Check for OAuth callback
  useEffect(() => {
    if (location.hash?.includes('session_id=')) {
      setIsOAuthCallback(true);
    }
  }, [location.hash]);

  // Check existing session
  useEffect(() => {
    // Skip if OAuth callback
    if (isOAuthCallback) {
      setLoading(false);
      return;
    }

    const checkSession = async () => {
      try {
        const response = await fetch(`${API_URL}/api/registry/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        }
      } catch (err) {
        console.warn('Session check failed:', err.message);
      } finally {
        setLoading(false);
      }
    };
    checkSession();
  }, [isOAuthCallback]);

  // Session timeout check - defined before useEffect that uses it
  const checkSessionTimeout = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/registry/session/info`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSessionInfo(data);
        
        // Show warning if less than 5 minutes remaining
        if (data.minutes_remaining <= 5 && data.minutes_remaining > 0) {
          setShowTimeoutWarning(prev => {
            if (!prev) {
              toast.warning(
                `Session expires in ${data.minutes_remaining} minute${data.minutes_remaining !== 1 ? 's' : ''}`,
                {
                  description: 'Your activity will refresh the timer',
                  duration: 10000,
                }
              );
            }
            return true;
          });
        } else {
          setShowTimeoutWarning(false);
        }
        
        // Auto-logout if expired
        if (data.seconds_remaining <= 0) {
          toast.error('Session expired', { description: 'Please log in again' });
          setUser(null);
        }
      }
    } catch (err) {
      console.warn('Session timeout check failed:', err.message);
    }
  }, []);

  // Fetch data when logged in
  useEffect(() => {
    if (user) {
      fetchSubmissions();
      fetchSubjects();
      fetchNotifications();
      fetchStats();
      if (user.role === 'administrator') {
        fetchStaffEmails();
        fetchAllowedDomains();
        fetchAllUsers();
      }
      
      // Start session timeout check
      checkSessionTimeout();
      sessionCheckRef.current = setInterval(checkSessionTimeout, 60000); // Check every minute
    }
    
    return () => {
      if (sessionCheckRef.current) {
        clearInterval(sessionCheckRef.current);
      }
    };
  }, [user, checkSessionTimeout]);

  // Clear selections when filter changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [statusFilter, searchTerm]);

  // Handle OAuth callback
  if (isOAuthCallback) {
    return <AuthCallback onAuthComplete={(userData) => {
      setUser(userData);
      setIsOAuthCallback(false);
    }} />;
  }

  const fetchSubmissions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/registry/submissions`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSubmissions(data.submissions || []);
      }
    } catch (err) {
      console.warn('Failed to fetch submissions:', err.message);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await fetch(`${API_URL}/api/registry/subjects`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSubjects(data.subjects || []);
      }
    } catch (err) {
      console.warn('Failed to fetch subjects:', err.message);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await fetch(`${API_URL}/api/registry/notifications`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (err) {
      console.warn('Failed to fetch notifications:', err.message);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/registry/dashboard/stats`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.warn('Failed to fetch stats:', err.message);
    }
  };

  const fetchStaffEmails = async () => {
    try {
      const response = await fetch(`${API_URL}/api/registry/staff-emails`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStaffEmails(data.emails || []);
      }
    } catch (err) {
      console.warn('Failed to fetch staff emails:', err.message);
    }
  };

  const fetchAllowedDomains = async () => {
    try {
      const response = await fetch(`${API_URL}/api/registry/allowed-domains`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAllowedDomains(data.domains || []);
      }
    } catch (err) {
      console.warn('Failed to fetch allowed domains:', err.message);
    }
  };

  const fetchAllUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/registry/users`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAllUsers(data.users || []);
      }
    } catch (err) {
      console.warn('Failed to fetch users:', err.message);
    } finally {
      setUsersLoading(false);
    }
  };

  // Bulk action handlers
  const handleSelectAll = () => {
    const newStatus = filteredSubmissions.filter(s => s.status === 'new');
    if (selectedIds.size === newStatus.length && newStatus.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(newStatus.map(s => s.id)));
    }
  };

  const handleSelectOne = (id, status) => {
    if (status !== 'new') return; // Only allow selecting 'new' submissions
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const handleBulkAction = async (action) => {
    if (selectedIds.size === 0) return;
    
    if (!window.confirm(`Are you sure you want to ${action} ${selectedIds.size} submission(s)?`)) {
      return;
    }
    
    setBulkLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/registry/submissions/bulk`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          submission_ids: Array.from(selectedIds),
          action: action
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`${data.updated} submission(s) ${action === 'prescreen' ? 'prescreened' : 'declined'}`);
        setSelectedIds(new Set());
        fetchSubmissions();
        fetchStats();
      } else {
        const err = await response.json();
        alert(err.detail || 'Bulk action failed');
      }
    } catch (err) {
      alert('Error performing bulk action');
    } finally {
      setBulkLoading(false);
    }
  };

  // Staff email handlers
  const handleAddStaffEmail = async (e) => {
    e.preventDefault();
    if (!newStaffEmail) return;
    
    setStaffEmailLoading(true);
    setStaffEmailError('');
    
    try {
      const response = await fetch(`${API_URL}/api/registry/staff-emails`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: newStaffEmail })
      });
      
      if (response.ok) {
        setNewStaffEmail('');
        fetchStaffEmails();
      } else {
        const err = await response.json();
        setStaffEmailError(err.detail || 'Failed to add email');
      }
    } catch (err) {
      setStaffEmailError('Error adding email');
    } finally {
      setStaffEmailLoading(false);
    }
  };

  const handleRemoveStaffEmail = async (email) => {
    if (!window.confirm(`Remove ${email} from staff allow-list?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/registry/staff-emails/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        fetchStaffEmails();
      } else {
        const err = await response.json();
        alert(err.detail || 'Failed to remove email');
      }
    } catch (err) {
      alert('Error removing email');
    }
  };

  // CSV Export handler
  const handleExportCSV = async () => {
    if (exportReason.length < 10) {
      toast.error('Please provide a reason (at least 10 characters) for the export');
      return;
    }
    
    setExportLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/registry/submissions/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          reason: exportReason,
          status_filter: statusFilter !== 'all' ? statusFilter : null
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Create and download CSV file
        const blob = new Blob([data.csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `cdoc_submissions_export_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        
        setShowExportModal(false);
        setExportReason('');
        toast.success(`Exported ${data.count} records`, { description: 'This action has been audit logged' });
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Export failed');
      }
    } catch (err) {
      toast.error('Error exporting data');
    } finally {
      setExportLoading(false);
    }
  };

  // Domain management handlers
  const handleAddDomain = async (e) => {
    e.preventDefault();
    if (!newDomain) return;
    
    setDomainLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/registry/allowed-domains`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ domain: newDomain })
      });
      
      if (response.ok) {
        setNewDomain('');
        fetchAllowedDomains();
        toast.success(`Domain @${newDomain} added to allow-list`);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to add domain');
      }
    } catch (err) {
      toast.error('Error adding domain');
    } finally {
      setDomainLoading(false);
    }
  };

  const handleRemoveDomain = async (domain) => {
    if (!window.confirm(`Remove @${domain} from allowed domains? Users with this domain will no longer be able to sign in via Google OAuth.`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/registry/allowed-domains/${encodeURIComponent(domain)}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        fetchAllowedDomains();
        toast.success(`Domain @${domain} removed`);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to remove domain');
      }
    } catch (err) {
      toast.error('Error removing domain');
    }
  };

  // User role management handlers
  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      const response = await fetch(`${API_URL}/api/registry/users/${userId}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role: newRole })
      });
      
      if (response.ok) {
        fetchAllUsers();
        toast.success('User role updated');
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to update role');
      }
    } catch (err) {
      toast.error('Error updating role');
    }
  };

  const handleDeactivateUser = async (userId, email) => {
    if (!window.confirm(`Deactivate user ${email}? They will be logged out immediately.`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/registry/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        fetchAllUsers();
        toast.success('User deactivated');
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to deactivate user');
      }
    } catch (err) {
      toast.error('Error deactivating user');
    }
  };

  // Study templates handlers
  const fetchStudyTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/registry/study-templates`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStudyTemplates(data.templates || []);
      }
    } catch (err) {
      // Handle error silently
    } finally {
      setTemplatesLoading(false);
    }
  };

  // Audit report handler
  const handleGenerateAuditReport = async () => {
    if (auditReportReason.length < 10) {
      toast.error('Please provide a reason (at least 10 characters)');
      return;
    }
    
    setAuditReportLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/registry/audit/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          reason: auditReportReason,
          start_date: auditStartDate || null,
          end_date: auditEndDate || null
        })
      });
      
      if (response.ok) {
        // Download the PDF
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `cdoc_audit_report_${new Date().toISOString().split('T')[0]}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
        
        setShowAuditReportModal(false);
        setAuditReportReason('');
        setAuditStartDate('');
        setAuditEndDate('');
        toast.success('Audit report downloaded');
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to generate report');
      }
    } catch (err) {
      toast.error('Error generating audit report');
    } finally {
      setAuditReportLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/registry/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (err) {
      // Ignore
    }
    setUser(null);
  };

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const markNotificationRead = async (id) => {
    try {
      await fetch(`${API_URL}/api/registry/notifications/${id}/read`, {
        method: 'POST',
        credentials: 'include'
      });
      fetchNotifications();
    } catch (err) {
      // Handle error silently
    }
  };

  const refreshData = () => {
    fetchSubmissions();
    fetchSubjects();
    fetchStats();
  };

  // Filter submissions
  const filteredSubmissions = submissions.filter(s => {
    const matchesSearch = searchTerm === '' ||
      s.email_masked?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.condition?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || s.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1877F2]" />
      </div>
    );
  }

  if (!user) {
    return <RegistryLogin onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Reveal Detail Modal */}
      {selectedSubmission && (
        <RevealDetailModal
          submission={selectedSubmission}
          onClose={() => setSelectedSubmission(null)}
          onReveal={refreshData}
          onPrescreen={refreshData}
          onDecline={refreshData}
          onConsent={refreshData}
        />
      )}

      {/* Header */}
      <header className="bg-[#0a1628] text-white py-4 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-[#87CEEB]" />
            <div>
              <h1 className="text-xl font-bold">Registry Console</h1>
              <p className="text-white/60 text-xs">CDOC Clinical Research</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* AI Assistant Button */}
            <button
              onClick={() => setShowAIAssistant(true)}
              className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 rounded-lg transition-all text-sm font-medium"
              data-testid="ai-assistant-button"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z"/>
                <circle cx="8" cy="14" r="1"/>
                <circle cx="16" cy="14" r="1"/>
              </svg>
              AI Assistant
            </button>
            <button
              onClick={() => setActiveTab('notifications')}
              className="relative p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>
            <div className="text-right">
              <p className="text-sm font-medium">{user.name || user.email}</p>
              <p className="text-xs text-[#87CEEB]">{user.role}</p>
            </div>
            {user.picture && (
              <img src={user.picture} alt="" className="w-9 h-9 rounded-full border-2 border-[#87CEEB]" />
            )}
            <Button
              variant="outline"
              size="sm"
              className="border-white/30 text-white hover:bg-white/10"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Navigation Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {[
            { id: 'dashboard', icon: BarChart3, label: 'Dashboard' },
            { id: 'charts', icon: TrendingUp, label: 'Analytics' },
            { id: 'intake', icon: ClipboardList, label: 'Intake Queue' },
            { id: 'subjects', icon: UserCheck, label: 'Subjects' },
            { id: 'progress', icon: Activity, label: 'Progress' },
            { id: 'consent', icon: Pen, label: 'Consent' },
            { id: 'communications', icon: Mail, label: 'Comms' },
            { id: 'reminders', icon: Bell, label: 'Reminders' },
            { id: 'documents', icon: FolderOpen, label: 'Documents' },
            { id: 'milestones', icon: Flag, label: 'Milestones' },
            { id: 'calendar', icon: Calendar, label: 'Calendar' },
            { id: 'batch-import', icon: Users, label: 'Batch Import' },
            { id: 'audit', icon: Shield, label: 'Audit Log' },
            ...(user?.role === 'administrator' ? [{ id: 'settings', icon: Settings, label: 'Settings' }] : [])
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                if (tab.id === 'templates') fetchStudyTemplates();
              }}
              data-testid={`registry-tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-[#1877F2] text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.badge > 0 && (
                <span className="ml-1 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && stats && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-3xl font-bold text-gray-900">{stats.total_submissions}</p>
                      <p className="text-sm text-gray-500">Total Submissions</p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-3xl font-bold text-orange-600">{stats.new_submissions}</p>
                      <p className="text-sm text-gray-500">New / Pending</p>
                    </div>
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Clock className="w-6 h-6 text-orange-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-3xl font-bold text-green-600">{stats.enrolled}</p>
                      <p className="text-sm text-gray-500">Enrolled</p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-3xl font-bold text-purple-600">{stats.total_subjects}</p>
                      <p className="text-sm text-gray-500">Subjects</p>
                    </div>
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Users className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg">Recent Activity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {stats.recent_activity?.slice(0, 5).map((event, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-8 h-8 bg-[#1877F2]/10 rounded-full flex items-center justify-center">
                        <Activity className="w-4 h-4 text-[#1877F2]" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{event.action}</p>
                        <p className="text-xs text-gray-500">{event.target_type}: {event.target_id?.slice(0, 8)}...</p>
                      </div>
                      <p className="text-xs text-gray-400">
                        {new Date(event.occurred_at).toLocaleTimeString()}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Analytics/Charts Tab */}
        {activeTab === 'charts' && (
          <StudyDashboard stats={stats} submissions={submissions} />
        )}

        {/* Intake Queue Tab */}
        {activeTab === 'intake' && (
          <div className="space-y-4">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search by email, condition..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="submissions-search-input"
                    />
                  </div>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-4 py-2 border rounded-lg bg-white"
                    data-testid="submissions-status-filter"
                  >
                    <option value="all">All Status</option>
                    <option value="new">New</option>
                    <option value="prescreened">Prescreened</option>
                    <option value="enrolled">Enrolled</option>
                    <option value="declined">Declined</option>
                  </select>
                  <select
                    value={siteFilter}
                    onChange={(e) => setSiteFilter(e.target.value)}
                    className="px-4 py-2 border rounded-lg bg-white flex items-center"
                    data-testid="submissions-site-filter"
                  >
                    {sites.map(site => (
                      <option key={site.id} value={site.id}>{site.name}</option>
                    ))}
                  </select>
                  <Button
                    variant="outline"
                    onClick={() => setShowExportModal(true)}
                    className="border-green-300 text-green-700 hover:bg-green-50"
                    data-testid="export-csv-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Bulk Action Bar */}
            {selectedIds.size > 0 && (
              <Card className="border-0 shadow-sm bg-blue-50 border-blue-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CheckSquare className="w-5 h-5 text-blue-600" />
                      <span className="font-medium text-blue-900">
                        {selectedIds.size} submission{selectedIds.size > 1 ? 's' : ''} selected
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleBulkAction('prescreen')}
                        disabled={bulkLoading}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid="bulk-prescreen-btn"
                      >
                        {bulkLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                        Prescreen All
                      </Button>
                      <Button
                        onClick={() => handleBulkAction('decline')}
                        disabled={bulkLoading}
                        variant="outline"
                        className="text-red-600 border-red-300 hover:bg-red-50"
                        data-testid="bulk-decline-btn"
                      >
                        {bulkLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                        Decline All
                      </Button>
                      <Button
                        onClick={() => setSelectedIds(new Set())}
                        variant="ghost"
                        className="text-gray-600"
                        data-testid="clear-selection-btn"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Clear
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {filteredSubmissions.some(s => s.status === 'new') && (
                      <button
                        onClick={handleSelectAll}
                        className="p-1 hover:bg-gray-100 rounded"
                        title="Select all new submissions"
                        data-testid="select-all-checkbox"
                      >
                        {selectedIds.size === filteredSubmissions.filter(s => s.status === 'new').length && 
                         filteredSubmissions.filter(s => s.status === 'new').length > 0
                          ? <CheckSquare className="w-5 h-5 text-blue-600" />
                          : <Square className="w-5 h-5 text-gray-400" />
                        }
                      </button>
                    )}
                    <span>Intake Submissions</span>
                  </div>
                  <span className="text-sm font-normal text-gray-500">
                    {filteredSubmissions.length} records (masked)
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {filteredSubmissions.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No submissions found</p>
                  ) : (
                    filteredSubmissions.map((sub) => (
                      <div
                        key={sub.id}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        data-testid={`submission-row-${sub.id}`}
                      >
                        <div className="flex items-center gap-4">
                          {/* Checkbox for new submissions */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectOne(sub.id, sub.status);
                            }}
                            className={`p-1 rounded ${sub.status === 'new' ? 'hover:bg-gray-200 cursor-pointer' : 'cursor-not-allowed opacity-30'}`}
                            disabled={sub.status !== 'new'}
                            data-testid={`checkbox-${sub.id}`}
                          >
                            {selectedIds.has(sub.id) 
                              ? <CheckSquare className="w-5 h-5 text-blue-600" />
                              : <Square className="w-5 h-5 text-gray-400" />
                            }
                          </button>
                          
                          <div
                            className="flex items-center gap-4 cursor-pointer flex-1"
                            onClick={() => setSelectedSubmission(sub)}
                          >
                            <div className="w-10 h-10 bg-[#1877F2]/10 rounded-full flex items-center justify-center font-bold text-[#1877F2]">
                              {sub.first_initial}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">
                                {sub.first_initial}. {sub.last_name_masked}
                              </p>
                              <p className="text-sm text-gray-500">{sub.email_masked}</p>
                            </div>
                          </div>
                        </div>
                        <div 
                          className="flex items-center gap-4 cursor-pointer"
                          onClick={() => setSelectedSubmission(sub)}
                        >
                          <div className="text-right">
                            <p className="text-sm font-medium text-[#1877F2]">{sub.condition}</p>
                            <p className="text-xs text-gray-500">Age: {sub.age}</p>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            sub.status === 'new' ? 'bg-orange-100 text-orange-700' :
                            sub.status === 'prescreened' ? 'bg-blue-100 text-blue-700' :
                            sub.status === 'enrolled' ? 'bg-green-100 text-green-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {sub.status}
                          </span>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Export Modal */}
            {showExportModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <Card className="w-full max-w-md border-0 shadow-2xl">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center">
                        <Download className="w-5 h-5 mr-2 text-green-600" />
                        Export Submissions
                      </CardTitle>
                      <button onClick={() => setShowExportModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <p className="text-sm text-amber-800">
                        <AlertCircle className="w-4 h-4 inline mr-1" />
                        This export will be <strong>audit logged</strong> and contain only de-identified data (no emails, phones, or full names).
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Reason for Export (Required)
                      </label>
                      <Textarea
                        value={exportReason}
                        onChange={(e) => setExportReason(e.target.value)}
                        placeholder="Enter reason for export (min 10 characters)..."
                        rows={3}
                        data-testid="export-reason-input"
                      />
                    </div>
                    
                    <div className="text-sm text-gray-600">
                      <p>Filter: <strong>{statusFilter === 'all' ? 'All submissions' : statusFilter}</strong></p>
                      <p>Records to export: <strong>{filteredSubmissions.length}</strong></p>
                    </div>
                    
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => setShowExportModal(false)}>
                        Cancel
                      </Button>
                      <Button
                        onClick={handleExportCSV}
                        disabled={exportLoading || exportReason.length < 10}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid="confirm-export-btn"
                      >
                        {exportLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                        Export CSV
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}

        {/* Subjects Tab */}
        {activeTab === 'subjects' && (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center justify-between">
                <span>Subjects (De-identified)</span>
                <span className="text-sm font-normal text-gray-500">{subjects.length} records</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {subjects.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No subjects enrolled yet</p>
                ) : (
                  subjects.map((sub) => (
                    <div
                      key={sub.rid}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                    >
                      <div>
                        <p className="font-mono text-sm font-medium text-gray-900">{sub.ssid}</p>
                        <p className="text-xs text-gray-500">RID: {sub.rid}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm font-medium text-[#1877F2]">{sub.condition}</p>
                          <p className="text-xs text-gray-500">Age: {sub.age_band}</p>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          sub.status === 'enrolled' ? 'bg-green-100 text-green-700' :
                          sub.status === 'withdrawn' ? 'bg-red-100 text-red-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {sub.status}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Consent Tracking Tab */}
        {activeTab === 'consent' && (
          <ConsentTracking />
        )}

        {/* Progress Tracking Tab */}
        {activeTab === 'progress' && (
          <SubjectProgressTracking subjects={subjects} />
        )}

        {/* Automated Reminders Tab */}
        {activeTab === 'reminders' && (
          <AutomatedReminders />
        )}

        {/* Communication Log Tab */}
        {activeTab === 'communications' && (
          <CommunicationLog />
        )}

        {/* Milestone Templates Tab */}
        {activeTab === 'milestones' && (
          <MilestoneTemplates />
        )}

        {/* Document Storage Tab */}
        {activeTab === 'documents' && (
          <DocumentStorage />
        )}

        {/* Visit Calendar Tab */}
        {activeTab === 'calendar' && (
          <VisitCalendar />
        )}

        {/* Batch Subject Import Tab */}
        {activeTab === 'batch-import' && (
          <BatchSubjectImport />
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">Notifications</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {notifications.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No notifications</p>
                ) : (
                  notifications.map((notif) => (
                    <div
                      key={notif.id}
                      className={`flex items-center justify-between p-4 rounded-lg transition-colors ${
                        notif.read ? 'bg-gray-50' : 'bg-blue-50 border border-blue-100'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          notif.read ? 'bg-gray-200' : 'bg-[#1877F2]'
                        }`}>
                          <Bell className={`w-5 h-5 ${notif.read ? 'text-gray-500' : 'text-white'}`} />
                        </div>
                        <div>
                          <p className={`font-medium ${notif.read ? 'text-gray-600' : 'text-gray-900'}`}>
                            {notif.title}
                          </p>
                          <p className="text-sm text-gray-500">{notif.message}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-gray-400">
                          {new Date(notif.created_at).toLocaleString()}
                        </p>
                        {!notif.read && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => markNotificationRead(notif.id)}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Audit Tab */}
        {activeTab === 'audit' && (
          <div className="space-y-4">
            {/* Audit Report Button */}
            {user?.role === 'administrator' && (
              <Card className="border-0 shadow-sm bg-purple-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileCheck className="w-6 h-6 text-purple-600" />
                      <div>
                        <p className="font-medium text-purple-900">HIPAA Compliance Audit Report</p>
                        <p className="text-sm text-purple-700">Generate a downloadable PDF audit report for compliance documentation</p>
                      </div>
                    </div>
                    <Button
                      onClick={() => setShowAuditReportModal(true)}
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="generate-audit-report-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Generate Report
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg">Audit Log (Read-Only)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {stats?.recent_activity?.map((event, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                          <Activity className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{event.action}</p>
                          <p className="text-sm text-gray-500">
                            {event.target_type}: {event.target_id?.slice(0, 12)}...
                          </p>
                          {event.reason && (
                            <p className="text-xs text-gray-400 italic">Reason: {event.reason}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">{event.actor_id?.slice(0, 10)}...</p>
                        <p className="text-xs text-gray-400">
                          {new Date(event.occurred_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Audit Report Modal */}
            {showAuditReportModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <Card className="w-full max-w-md border-0 shadow-2xl">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center">
                        <FileCheck className="w-5 h-5 mr-2 text-purple-600" />
                        Generate Audit Report
                      </CardTitle>
                      <button onClick={() => setShowAuditReportModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                      <p className="text-sm text-purple-800">
                        <Shield className="w-4 h-4 inline mr-1" />
                        This generates a HIPAA-compliant PDF audit report. The action will be logged.
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Date (Optional)</label>
                        <Input
                          type="date"
                          value={auditStartDate}
                          onChange={(e) => setAuditStartDate(e.target.value)}
                          data-testid="audit-start-date"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">End Date (Optional)</label>
                        <Input
                          type="date"
                          value={auditEndDate}
                          onChange={(e) => setAuditEndDate(e.target.value)}
                          data-testid="audit-end-date"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Reason for Report (Required)
                      </label>
                      <Textarea
                        value={auditReportReason}
                        onChange={(e) => setAuditReportReason(e.target.value)}
                        placeholder="Enter reason for audit report (min 10 characters)..."
                        rows={3}
                        data-testid="audit-report-reason"
                      />
                    </div>
                    
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => setShowAuditReportModal(false)}>
                        Cancel
                      </Button>
                      <Button
                        onClick={handleGenerateAuditReport}
                        disabled={auditReportLoading || auditReportReason.length < 10}
                        className="bg-purple-600 hover:bg-purple-700"
                        data-testid="confirm-audit-report-btn"
                      >
                        {auditReportLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                        Download PDF
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}

        {/* Study Templates Tab */}
        {activeTab === 'templates' && (
          <div className="space-y-4">
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <div className="flex items-center">
                    <FileText className="w-5 h-5 mr-2 text-[#1877F2]" />
                    Study Protocol Templates
                  </div>
                  <span className="text-sm font-normal text-gray-500">
                    {studyTemplates.length} templates available
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-blue-800">
                    <Shield className="w-4 h-4 inline mr-1" />
                    Pre-configured templates for common clinical research protocols. Use these as starting points for new studies.
                  </p>
                </div>

                {templatesLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {studyTemplates.map((template) => (
                      <div
                        key={template.id}
                        className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer border border-transparent hover:border-[#1877F2]"
                        onClick={() => setSelectedTemplate(template)}
                        data-testid={`template-card-${template.id}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h3 className="font-medium text-gray-900">{template.name}</h3>
                            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">{template.category}</span>
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{template.description}</p>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {template.estimated_duration_weeks} weeks
                          </span>
                          <span className="flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            {template.inclusion_criteria?.length || 0} inclusion criteria
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Template Detail Modal */}
            {selectedTemplate && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto border-0 shadow-2xl">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg">{selectedTemplate.name}</CardTitle>
                        <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">{selectedTemplate.category}</span>
                      </div>
                      <button onClick={() => setSelectedTemplate(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-gray-600">{selectedTemplate.description}</p>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500">Duration</p>
                        <p className="font-medium">{selectedTemplate.estimated_duration_weeks} weeks</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500">Visit Schedule</p>
                        <p className="font-medium text-sm">{selectedTemplate.visit_schedule}</p>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                        Inclusion Criteria
                      </h4>
                      <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                        {selectedTemplate.inclusion_criteria?.map((c, i) => (
                          <li key={i}>{c}</li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                        <XCircle className="w-4 h-4 mr-2 text-red-600" />
                        Exclusion Criteria
                      </h4>
                      <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                        {selectedTemplate.exclusion_criteria?.map((c, i) => (
                          <li key={i}>{c}</li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                        <ClipboardList className="w-4 h-4 mr-2 text-blue-600" />
                        Required Assessments
                      </h4>
                      <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                        {selectedTemplate.required_assessments?.map((a, i) => (
                          <li key={i}>{a}</li>
                        ))}
                      </ul>
                    </div>

                    <div className="flex gap-2 justify-end pt-4 border-t">
                      <Button variant="outline" onClick={() => setSelectedTemplate(null)}>
                        Close
                      </Button>
                      <Button className="bg-[#1877F2] hover:bg-[#166FE5]">
                        <FileCheck className="w-4 h-4 mr-2" />
                        Use Template
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}

        {/* Settings Tab (Admin Only) */}
        {activeTab === 'settings' && user?.role === 'administrator' && (
          <div className="space-y-6">
            {/* Allowed Domains */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center">
                  <Globe className="w-5 h-5 mr-2 text-green-600" />
                  Allowed Domains (Google OAuth)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-800">
                    <Shield className="w-4 h-4 inline mr-1" />
                    Anyone with an email from these domains can access the Registry Console via Google OAuth.
                  </p>
                </div>

                {/* Add Domain Form */}
                <form onSubmit={handleAddDomain} className="flex gap-2">
                  <div className="flex-1 flex items-center">
                    <span className="bg-gray-100 border border-r-0 border-gray-300 rounded-l-md px-3 py-2 text-gray-500">@</span>
                    <Input
                      type="text"
                      value={newDomain}
                      onChange={(e) => setNewDomain(e.target.value.toLowerCase())}
                      placeholder="company.com"
                      className="rounded-l-none"
                      data-testid="add-domain-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={domainLoading || !newDomain}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="add-domain-btn"
                  >
                    {domainLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                    Add
                  </Button>
                </form>

                {/* Domain List */}
                <div className="space-y-2">
                  {allowedDomains.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">No domains configured</p>
                  ) : (
                    allowedDomains.map((domain, idx) => (
                      <div
                        key={`domain-${domain}`}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        data-testid={`domain-row-${domain}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                            <Globe className="w-4 h-4 text-green-600" />
                          </div>
                          <span className="font-medium text-gray-900">@{domain}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveDomain(domain)}
                          className="text-red-600 hover:bg-red-50"
                          data-testid={`remove-domain-btn-${idx}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>

                <p className="text-xs text-gray-500">
                  {allowedDomains.length} domain{allowedDomains.length !== 1 ? 's' : ''} allowed
                </p>
              </CardContent>
            </Card>

            {/* Staff Email Allow-list */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center">
                  <Mail className="w-5 h-5 mr-2 text-[#1877F2]" />
                  Individual Staff Emails (Google OAuth)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <Shield className="w-4 h-4 inline mr-1" />
                    Individual emails that can access the Registry Console (in addition to allowed domains).
                  </p>
                </div>

                {/* Add Email Form */}
                <form onSubmit={handleAddStaffEmail} className="flex gap-2">
                  <Input
                    type="email"
                    value={newStaffEmail}
                    onChange={(e) => setNewStaffEmail(e.target.value)}
                    placeholder="Enter staff email address"
                    className="flex-1"
                    data-testid="add-staff-email-input"
                  />
                  <Button
                    type="submit"
                    disabled={staffEmailLoading || !newStaffEmail}
                    className="bg-[#1877F2] hover:bg-[#166FE5]"
                    data-testid="add-staff-email-btn"
                  >
                    {staffEmailLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                    Add
                  </Button>
                </form>

                {staffEmailError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm flex items-center">
                    <AlertCircle className="w-4 h-4 mr-2" />
                    {staffEmailError}
                  </div>
                )}

                {/* Email List */}
                <div className="space-y-2">
                  {staffEmails.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">No individual staff emails configured</p>
                  ) : (
                    staffEmails.map((email, idx) => (
                      <div
                        key={`email-${email}`}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        data-testid={`staff-email-row-${email}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-[#1877F2]/10 rounded-full flex items-center justify-center">
                            <Mail className="w-4 h-4 text-[#1877F2]" />
                          </div>
                          <span className="font-medium text-gray-900">{email}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveStaffEmail(email)}
                          className="text-red-600 hover:bg-red-50"
                          disabled={staffEmails.length <= 1}
                          title={staffEmails.length <= 1 ? "Cannot remove the last email" : "Remove email"}
                          data-testid={`remove-email-btn-${idx}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>

                <p className="text-xs text-gray-500">
                  {staffEmails.length} email{staffEmails.length !== 1 ? 's' : ''} in allow-list
                </p>
              </CardContent>
            </Card>

            {/* User Role Management */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center">
                  <UserCog className="w-5 h-5 mr-2 text-purple-600" />
                  User Role Management
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <p className="text-sm text-purple-800">
                    <Shield className="w-4 h-4 inline mr-1" />
                    Assign roles to control what each staff member can do in the system.
                  </p>
                </div>

                <div className="text-xs text-gray-600 grid grid-cols-2 md:grid-cols-5 gap-2 p-3 bg-gray-50 rounded-lg">
                  <div><strong>Administrator:</strong> Full access</div>
                  <div><strong>Coordinator:</strong> Intake & subjects</div>
                  <div><strong>PI:</strong> Data export & withdraw</div>
                  <div><strong>Data Manager:</strong> View subjects</div>
                  <div><strong>Broker:</strong> Identity crosswalk</div>
                </div>

                {usersLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : (
                  <div className="space-y-2">
                    {allUsers.length === 0 ? (
                      <p className="text-center text-gray-500 py-4">No users found</p>
                    ) : (
                      allUsers.map((u) => (
                        <div
                          key={u.user_id}
                          className={`flex items-center justify-between p-3 rounded-lg ${u.active ? 'bg-gray-50' : 'bg-red-50 opacity-60'}`}
                          data-testid={`user-row-${u.user_id}`}
                        >
                          <div className="flex items-center gap-3">
                            {u.picture ? (
                              <img src={u.picture} alt="" className="w-8 h-8 rounded-full" />
                            ) : (
                              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                                <User className="w-4 h-4 text-purple-600" />
                              </div>
                            )}
                            <div>
                              <p className="font-medium text-gray-900">{u.name || u.email}</p>
                              <p className="text-xs text-gray-500">{u.email}</p>
                            </div>
                            {!u.active && <span className="text-xs bg-red-200 text-red-700 px-2 py-0.5 rounded">Deactivated</span>}
                          </div>
                          <div className="flex items-center gap-2">
                            <select
                              value={u.role}
                              onChange={(e) => handleUpdateUserRole(u.user_id, e.target.value)}
                              className="text-sm border rounded-md px-2 py-1 bg-white"
                              disabled={u.user_id === user.user_id || !u.active}
                              data-testid={`role-select-${u.user_id}`}
                            >
                              <option value="administrator">Administrator</option>
                              <option value="coordinator">Coordinator</option>
                              <option value="pi">PI</option>
                              <option value="data_manager">Data Manager</option>
                              <option value="broker">Broker</option>
                            </select>
                            {u.user_id !== user.user_id && u.active && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeactivateUser(u.user_id, u.email)}
                                className="text-red-600 hover:bg-red-50"
                                title="Deactivate user"
                                data-testid={`deactivate-user-btn-${u.user_id}`}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                <p className="text-xs text-gray-500">
                  {allUsers.filter(u => u.active).length} active user{allUsers.filter(u => u.active).length !== 1 ? 's' : ''}
                </p>
              </CardContent>
            </Card>

            {/* Session Info */}
            {sessionInfo && (
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center">
                    <Timer className="w-5 h-5 mr-2 text-orange-600" />
                    Current Session
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500">Session Time Remaining</p>
                      <p className={`text-lg font-bold ${sessionInfo.minutes_remaining <= 5 ? 'text-red-600' : 'text-gray-900'}`}>
                        {sessionInfo.minutes_remaining} min
                      </p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500">Role</p>
                      <p className="text-lg font-bold text-[#1877F2] capitalize">{sessionInfo.role}</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500">Last Activity</p>
                      <p className="text-sm font-medium">{new Date(sessionInfo.last_activity).toLocaleTimeString()}</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500">Expires At</p>
                      <p className="text-sm font-medium">{new Date(sessionInfo.expires_at).toLocaleTimeString()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* AI Assistant Panel */}
      <AIAssistantPanel 
        isOpen={showAIAssistant} 
        onClose={() => setShowAIAssistant(false)}
        context={{
          currentTab: activeTab,
          userRole: user?.role,
          site: 'CDOC Clinical Research'
        }}
      />
    </div>
  );
};

export default RegistryPage;
